import React, { useState, useRef, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { DialogType } from 'office-ui-fabric-react/lib/Dialog';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { useSelector, useDispatch } from 'react-redux';
import { submitForApproval } from '../../actions/agreements';
import TextButton from '../../components/TextButton';
import AgreementForm from '../../components/AgreementForm';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import SubModule from '../../components/SubModule';
import SubModuleContent from '../../components/SubModuleContent';
import PauseTransactionsDialog from '../../components/PauseTransactionsDialog';
import Main from '../../components/Main';
import PageTitle from '../../components/PageTitle';
import SavingIndicator from '../../components/SavingIndicator';
import styles from './EditAgreement.style';

const EditAgreement = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const history = useHistory();
  const ref = useRef();
  const { contractAddress, agreementAddress } = useParams();
  const agreement = useSelector(state => get(state, `entities.agreements.${agreementAddress}`, {}));
  const dispatch = useDispatch();

  // if the agreement isn't approved or in draft, then you shouldn't be able to
  // edit it here in this user flow
  useEffect(() => {
    if (
      agreement.approvalStatus &&
      !['Draft', 'Approved'].includes(agreement.approvalStatus) &&
      !isSubmitting
    ) {
      history.push(`/contracts/${contractAddress}`);
    }
  }, [history, agreement.approvalStatus, contractAddress, isSubmitting]);

  const handleCancel = () => {
    history.push(`/contracts/${contractAddress}`);
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    if (ref.current.dirty) {
      // agreement forms may `throw` errors, so they bubble up, so we need to
      // catch them. However, we can't do anything with them, and don't need to
      // here anyway
      // https://github.com/jaredpalmer/formik/pull/2420
      await ref.current.submitForm().catch(() => {});
      if (ref.current && !isEmpty(ref.current.errors)) {
        setIsSubmitting(false);
      }
    } else {
      const invalidFields = await ref.current.validateForm();
      if (isEmpty(invalidFields)) {
        history.push(`/contracts/${contractAddress}`);
      } else {
        setIsSubmitting(false);
      }
    }
  };

  const handleSubmit = () => {
    const criticalFields = ['effectiveDate', 'terminationDate', 'rate'];
    const updatedFields = Object.keys(ref.current.touched);
    const hasCriticalEdits = updatedFields.some(field => criticalFields.includes(field));
    if (agreement.approvalStatus === 'Approved' && hasCriticalEdits) {
      setIsDialogVisible(true);
    } else {
      handleSave();
    }
  };

  const handleSuccess = async updatedAgreement => {
    setIsDialogVisible(false);

    // on the edit agreement page, if the agreement is a draft, we want to submit
    // it for approval after save. There's currently no UX option to save in draft.
    if (updatedAgreement.approvalStatus === 'Draft') {
      // Submit for approval should go against updatedAgreement agreementAddress
      const response = await dispatch(submitForApproval(contractAddress, updatedAgreement.address));

      if (!response.error) {
        history.push(`/contracts/${contractAddress}`);
      } else {
        setIsSubmitting(false);
      }
    } else {
      history.push(`/contracts/${contractAddress}`);
    }
  };

  return (
    <>
      <PageTitle>Edit Royalty Fee</PageTitle>

      <Main>
        <Module>
          <ModuleContent>
            <SubModule>
              <SubModuleContent>
                <AgreementForm
                  address={agreementAddress}
                  contractAddress={contractAddress}
                  onSuccess={handleSuccess}
                  ref={ref}
                />
              </SubModuleContent>
            </SubModule>
          </ModuleContent>
        </Module>

        <div className={styles.buttonContainer}>
          <TextButton
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </TextButton>
          {!isSubmitting ? (
            <PrimaryButton type="submit" onClick={handleSubmit} disabled={isSubmitting}>
              Submit for Approval
            </PrimaryButton>
          ) : (
            <SavingIndicator />
          )}
        </div>

        <PauseTransactionsDialog
          onDismiss={() => setIsDialogVisible(false)}
          onSubmit={handleSave}
          isSubmitting={isSubmitting}
          name={get(ref, 'current.values.name') || agreement.name}
          hidden={!isDialogVisible}
          dialogContentProps={{
            type: DialogType.normal,
            title: 'Pause/Reprocess Transactions',
            closeButtonAriaLabel: 'Close',
          }}
        />
      </Main>
    </>
  );
};

export default EditAgreement;
